/* eslint-disable @typescript-eslint/strict-boolean-expressions */

/**
 * This file contains various hacks made to our dependencies.
 *
 * These would be better as modifications to the libraries themselves, ie. we should fork the libary, but take this form instead in the interest of time.
 * These may break if the libraries they modify are updated.
 */

import { type Transform, type Map } from "maplibre-gl"
import Point from "@mapbox/point-geometry"
import { getViewRect } from "./getViewableRect"

/**
 * Change the "fit function" of a map's max bounds from "cover" to "contain"
 *
 * By default, maplibre-gl's map max bounds behave similar to the object-fit: cover CSS rule.
 * This changes the behavior to match the object-fit: contain CSS rule.
 * The reason this exists is so that users can zoom out to view an entire state at once.
 * May break if the maplibre-gl library is updated. Working on maplibre-gl version 3.1.0
 *
 * @todo fork maplibre-gl and add these changes as a new feature
 * @param map Map to modify
 */
export function mapBoundsContainHack(map: Map): void {
    function mercatorXfromLng(lng: number): number {
        return (180 + lng) / 360
    }

    function mercatorYfromLat(lat: number): number {
        return (
            (180 -
                (180 / Math.PI) *
                    Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360))) /
            360
        )
    }

    /**
     * constrain n to the given range, excluding the minimum, via modular arithmetic
     *
     * @param n value
     * @param min the minimum value to be returned, exclusive
     * @param max the maximum value to be returned, inclusive
     * @returns constrained number
     * @private
     */
    function wrap(n: number, min: number, max: number): number {
        const d = max - min
        const w = ((((n - min) % d) + d) % d) + min
        return w === min ? max : w
    }

    // Overwrite the Transform._constrain function
    map.transform._constrain = function (this: Transform) {
        // Demo code to show how perfect this is
        // document.querySelector("#map").parentElement.style.zIndex="10000000"; debug_map.setPitch(0)

        if (!this.center || !this.width || !this.height || this._constraining)
            return

        this._constraining = true

        let minY = -90
        let maxY = 90
        let minX = -180
        let maxX = 180
        let sy, sx, x2, y2
        const size = this.size

        const unmodified = this._unmodified

        const viewport = getViewRect()
        const viewportHeight = viewport.bottom - viewport.top
        const viewportWidth = viewport.right - viewport.left
        const viewportCenter = new Point(
            (viewport.left + viewport.right) / 2,
            (viewport.top + viewport.bottom) / 2
        )

        // TODO: Account for nudge
        const mapRect = document
            .querySelector("#map canvas")
            ?.getBoundingClientRect()

        if (typeof mapRect === "undefined") {
            throw new Error("Map canvas not found")
        }

        const mapCenter = new Point(
            (mapRect.left + mapRect.right) / 2,
            (mapRect.top + mapRect.bottom) / 2
        )

        const centerDiff = mapCenter.sub(viewportCenter)

        const dl = viewport.left - mapRect.left
        const dr = mapRect.right - viewport.right
        const dt = viewport.top - mapRect.top
        const db = mapRect.bottom - viewport.bottom
        const ol = (dl * size.x) / (size.x - dl)
        const or = (dr * size.x) / (size.x - dr)
        const ot = (dt * size.y) / (size.y - dt)
        const ob = (db * size.y) / (size.y - db)

        const dx = ol + or
        const dy = ot + ob

        if (this.latRange) {
            const latRange = this.latRange
            minY = mercatorYfromLat(latRange[1]) * this.worldSize
            maxY = mercatorYfromLat(latRange[0]) * this.worldSize
            sy =
                maxY - minY < viewportHeight
                    ? viewportHeight / (maxY - minY)
                    : 0
        }

        if (this.lngRange) {
            const lngRange = this.lngRange

            minX = wrap(
                mercatorXfromLng(lngRange[0]) * this.worldSize,
                0,
                this.worldSize
            )
            maxX = wrap(
                mercatorXfromLng(lngRange[1]) * this.worldSize,
                0,
                this.worldSize
            )

            if (maxX < minX) maxX += this.worldSize

            // English description
            // If the pixel bounds span less width than the screen,
            // find the ratio of screen width to bounds width.

            sx = maxX - minX < viewportWidth ? viewportWidth / (maxX - minX) : 0
        }

        const point = this.point

        // how much the map should scale to fit the screen into given latitude/longitude ranges
        const s =
            viewportHeight / viewportWidth > (maxY - minY) / (maxX - minX)
                ? sx
                : sy // Math.max(sx || 0, sy || 0)

        if (s) {
            // Set the map center (lngLat) such that the center of our bounds is in the center of the viewport

            this.center = this.unproject(
                new Point(
                    sx ? (maxX + minX) / 2 : point.x,
                    sy ? (maxY + minY) / 2 : point.y
                ).add(centerDiff)
            )
            this.zoom += this.scaleZoom(s)
            this._unmodified = unmodified
            this._constraining = false
        }

        if (this.latRange) {
            // y is the map point at the center of the viewport
            const y = point.y - centerDiff.y
            const h2 = viewportHeight / 2

            // const ob = 0

            const top = y - h2
            const bottom = y + h2

            // English description
            // If the bottom viewport bound exceeds the bottom pixel bound,
            // move the screen back so that the bottom viewport bound equals the bottom pixel bound

            if (viewportHeight > maxY - minY) {
                y2 = (minY + maxY) / 2 + centerDiff.y
            } else {
                if (top < minY) y2 = minY + h2 + centerDiff.y
                if (bottom > maxY) y2 = maxY - h2 + centerDiff.y
            }
        }

        if (this.lngRange) {
            const centerX = (minX + maxX) / 2 + centerDiff.x
            const x = wrap(
                point.x - centerDiff.x,
                centerX - this.worldSize / 2,
                centerX + this.worldSize / 2
            )
            const w2 = viewportWidth / 2

            if (viewportWidth > maxX - minX) {
                x2 = centerX
            } else {
                if (x - w2 < minX) x2 = minX + w2 + centerDiff.x
                if (x + w2 > maxX) x2 = maxX - w2 + centerDiff.x
            }
        }

        // pan the map if the screen goes off the range
        if (x2 !== undefined || y2 !== undefined) {
            this.center = this.unproject(
                new Point(
                    x2 !== undefined ? x2 : point.x,
                    y2 !== undefined ? y2 : point.y
                )
            ).wrap()
        }

        this._unmodified = unmodified
        this._constraining = false
    }.bind(map.transform)
}
